import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(20, 16, 40, 0.6);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2600;
`;

const ModalCard = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 360px;
  border-radius: 28px;
  padding: 32px 26px;
  background: var(--modal-bg);
  box-shadow:
    0 30px 60px rgba(27, 19, 63, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  overflow: hidden;

  [data-theme="dark"] & {
    box-shadow:
      0 30px 60px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }
`;

const GradientBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #7b68ee, #87ceeb, #9966cc);
  background-size: 200% 100%;
  animation: shimmer 4s ease infinite;

  @keyframes shimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const IconCircle = styled(motion.div)`
  width: 82px;
  height: 82px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7b68ee, #9966cc);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 42px;
  box-shadow: 0 14px 30px rgba(123, 104, 238, 0.35);
`;

const Title = styled.h3`
  margin: 0 0 8px;
  text-align: center;
  font-size: 22px;
  font-weight: 800;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  margin: 0 0 28px;
  text-align: center;
  font-size: 15px;
  line-height: 1.6;
  color: var(--text-secondary);
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Button = styled(motion.button)<{ $variant: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 16px 20px;
  border-radius: 18px;
  border: none;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, #7b68ee, #9966cc)'
    : 'rgba(123, 104, 238, 0.08)'};
  color: ${props => props.$variant === 'primary' ? '#ffffff' : '#7b68ee'};
  box-shadow: ${props => props.$variant === 'primary'
    ? '0 12px 24px rgba(123, 104, 238, 0.35)'
    : 'inset 0 0 0 1px rgba(123, 104, 238, 0.2)'};

  [data-theme="dark"] & {
    background: ${props => props.$variant === 'primary'
      ? 'linear-gradient(135deg, #9d8eee, #b896dc)'
      : 'rgba(157, 142, 238, 0.15)'};
    color: ${props => props.$variant === 'primary' ? '#ffffff' : '#C4B5FD'};
    box-shadow: ${props => props.$variant === 'primary'
      ? '0 12px 24px rgba(157, 142, 238, 0.4)'
      : 'inset 0 0 0 1px rgba(157, 142, 238, 0.3)'};
  }
`;

const CelebrateWrapper = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

const CelebrateTitle = styled.h3`
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(135deg, #34d399, #22c55e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CelebrateText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  color: var(--text-secondary);
`;

const ConfettiLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const ConfettiPiece = styled(motion.span)<{ $color: string }>`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${props => props.$color};
  opacity: 0;
`;

const confettiPalette =['#7b68ee', '#9966cc', '#87ceeb', '#34d399', '#facc15'];

interface ApplicationModalProps {
  date: string;
  onClose: () => void;
  onConfirm: (applied: boolean) => void;
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({ date, onClose, onConfirm }) => {
  const[phase, setPhase] = useState<'question' | 'celebrate'>('question');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const formattedDate = useMemo(() => {
    const dateStr = new Date(`${date}T00:00:00`).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      weekday: 'long'
    });
    // Первая буква заглавная
    return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
  },[date]);

  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  },[]);

  const handleConfirm = (applied: boolean) => {
    if (applied) {
      setPhase('celebrate');
      onConfirm(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setPhase('question');
        onClose();
      }, 900);
    } else {
      onConfirm(false);
      onClose();
    }
  };

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <ModalCard
        initial={{ scale: 0.94, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.94, opacity: 0, y: 24 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <GradientBar />

        {phase === 'question' && (
          <>
            <IconCircle
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 380, damping: 18 }}
            >
              💧
            </IconCircle>
            <Title>{formattedDate}</Title>
            <Subtitle>Применили ли Вы ретинол сегодня?</Subtitle>
            <ButtonGroup>
              <Button
                $variant="primary"
                onClick={() => handleConfirm(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
              >
                Да, уже применил(а)
              </Button>
              <Button
                $variant="secondary"
                onClick={() => handleConfirm(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
              >
                Ещё нет
              </Button>
            </ButtonGroup>
          </>
        )}

        {phase === 'celebrate' && (
          <CelebrateWrapper>
            <ConfettiLayer>
              {Array.from({ length: 32 }).map((_, index) => (
                <ConfettiPiece
                  key={index}
                  $color={confettiPalette[index % confettiPalette.length]}
                  initial={{
                    x: '50%',
                    y: '50%',
                    scale: 0,
                    opacity: 0
                  }}
                  animate={{
                    x: `${Math.random() * 120 - 10}%`,
                    y: `${Math.random() * 120 - 10}%`,
                    opacity:[0, 1, 0],
                    scale:[0, 1, 0.4],
                    rotate: Math.random() * 360
                  }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                />
              ))}
            </ConfettiLayer>
            <IconCircle
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 360, damping: 16 }}
            >
              🎉
            </IconCircle>
            <CelebrateTitle>Отлично!</CelebrateTitle>
            <CelebrateText>Мы отметили применение ретинола. Продолжайте сиять!</CelebrateText>
          </CelebrateWrapper>
        )}
      </ModalCard>
    </Overlay>
  );
};

export default ApplicationModal;